import {Flex} from 'src/design/atoms/Flex';
import {Tooltip} from 'src/design/atoms/Tooltip';

import React from 'react';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {SecondaryActionPanel} from 'src/design/molecules/SecondaryActionPanel';
import {AcknowledgementTransitionButton} from './AcknowledgementTransitionButton';
import type {emptySplitApi} from 'src/redux/api';
import type {
  TransitioningResponse} from 'src/shared/hooks/api/useStateTransitions';
import {
  useStateTransitions,
} from 'src/shared/hooks/api/useStateTransitions';
import type {IStateMachine, IStateMachineTransition} from 'src/types/StateMachine';

type TransitionInvalidateTag = Parameters<
  typeof emptySplitApi.util.invalidateTags
>[0][number];

// Re-export for backwards compatibility
export type {TransitioningResponse as ITransitioningResponse};

export interface FormTransitionBarProps {
  showActionBar?: boolean;
  title?: string;
  description?: string;

  stateMachine: IStateMachine;
  stateId: number;
  transitionBaseUrl: string;
  onTransitioning?: (
    transition: IStateMachineTransition
  ) => Promise<TransitioningResponse>;
  /** Posts the transition instead of ApiCaller — the offline queueing seam. */
  postTransition?: (args: {
    transition: string;
    note: string;
    occurredAt: string;
  }) => Promise<{offlineQueued?: boolean}>;
  /** Called instead of `onRefreshTransition` when the save was queued offline. */
  onOfflineQueued?: (transition: IStateMachineTransition) => void;
  onTransitioned?: (transition: IStateMachineTransition) => void;
  onCustomHandlerTransition?: (transition: IStateMachineTransition) => void;
  onRefreshTransition: (transition: IStateMachineTransition) => void;

  transitionFormUrl?: string;

  objectType?: number;

  module?: ModuleEnum;

  preTransitionButtonNodes?: React.ReactNode[];
  postTransitionButtonNodes?: React.ReactNode[];

  bypassPermissions?: boolean;

  /** RTK Query tags to invalidate after a successful transition. */
  invalidateTagsOnComplete?: TransitionInvalidateTag[];

  /** Back fallback for `redirect-previous` transitions; defaults to the dashboard. */
  fallbackTo?: string;
  /** Replaces navigation for redirect transitions (see `useStateTransitions`). */
  onRedirect?: (transition: IStateMachineTransition) => void;
}

export const FormTransitionBar: React.FC<FormTransitionBarProps> = ({
  showActionBar = false,
  title,
  description,
  stateMachine,
  stateId,
  transitionBaseUrl,
  transitionFormUrl,
  onTransitioning,
  postTransition,
  onOfflineQueued,
  onTransitioned,
  onCustomHandlerTransition,
  onRefreshTransition,
  objectType,
  module,
  preTransitionButtonNodes,
  postTransitionButtonNodes,
  bypassPermissions,
  invalidateTagsOnComplete,
  fallbackTo,
  onRedirect,
}) => {
  // Use the shared hook for transition logic
  const {
    availableTransitions,
    currentState,
    isTransitioning,
    executeTransition,
  } = useStateTransitions({
    stateMachine,
    stateId,
    transitionBaseUrl,
    module: module ?? ModuleEnum.Invalid,
    objectType,
    bypassPermissions: bypassPermissions || !module, // Bypass if no module provided
    onTransitioning,
    postTransition,
    onOfflineQueued,
    onTransitioned,
    onCustomHandlerTransition,
    transitionFormUrl,
    onTransitionComplete: onRefreshTransition,
    invalidateTagsOnComplete,
    fallbackTo,
    onRedirect,
  });

  const renderBar = () => (
    <Flex w="100%" direction={{base: 'column', md: 'row'}} gap="md">
      {preTransitionButtonNodes}
      {currentState &&
        availableTransitions.map((transition: IStateMachineTransition) => (
          <AcknowledgementTransitionButton
            key={transition.name}
            transition={transition}
            executeTransition={executeTransition}
            renderButton={onClick => (
              <Tooltip label={transition.description ?? transition.title}>
                <SaveButton
                  loading={isTransitioning}
                  mr="md"
                  onClick={isTransitioning ? undefined : onClick}
                  color={transition.color ?? 'yellow'}
                  disabled={isTransitioning}>
                  {transition.title}
                </SaveButton>
              </Tooltip>
            )}
          />
        ))}
      {postTransitionButtonNodes}
    </Flex>
  );

  const transLength = availableTransitions.length;
  const preTransLength = preTransitionButtonNodes?.length ?? 0;
  const postTransLength = postTransitionButtonNodes?.length ?? 0;
  if (transLength === 0 && preTransLength === 0 && postTransLength === 0) {
    return <></>;
  }

  if (showActionBar || title || description) {
    return (
      <SecondaryActionPanel title={title} description={description}>
        {renderBar()}
      </SecondaryActionPanel>
    );
  }

  return renderBar();
};
